import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

export const dropbox = defineConnector({
  identifier: "dropbox",
  name: "Dropbox",
  tagLine: "Cloud file storage and sharing",
  keywords: "cloud storage files folders sharing documents",
  logo,
  protocol: "http",
  tags: ["documents", "data"],
  defaultUrlPattern: () =>
    "https://(?:api\\.dropboxapi\\.com/.*|content\\.dropboxapi\\.com/.*)",
  auth: (app) =>
    oauthAuth.withHeaders([
      {
        name: "Dropbox-API-Path-Root",
        value: `{".tag":"namespace_id","namespace_id":"${app.config.namespaceId}"}`,
      },
    ]),
  steps: [
    {
      title: "Create a Dropbox app",
      items: [
        {
          kind: "markdown",
          content:
            "Create a [Dropbox app](https://www.dropbox.com/developers/apps) with **‘Scoped access’**. Choose **‘App folder’** for a dedicated folder or **‘Full Dropbox’** for account-wide access to existing content.",
        },
      ],
    },
    {
      title: "Configure the redirect URI",
      items: [
        {
          kind: "markdown",
          content:
            "In your app's settings, go to the **'OAuth 2'** section and add the following redirect URI:",
        },
        {
          kind: "copyable",
          label: "Redirect URI",
          value: "{API_URL}/connected-apps/oauth/dropbox/callback",
        },
      ],
    },
    {
      title: "Copy your app credentials",
      items: [
        {
          kind: "markdown",
          content:
            "From your app's settings page, copy the **App key** and **App secret**.",
        },
        {
          kind: "input",
          label: "App key",
          placeholder: "Paste your App key",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "App secret",
          placeholder: "Paste your App secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Configure permissions",
      items: [
        {
          kind: "markdown",
          content:
            "In **‘Permissions’**, enable and submit the scopes your workflows need. Scope names are case-sensitive; paste them below, separated by spaces.\n\nTo find a root namespace ID, open Dropbox’s [API Explorer](https://dropbox.github.io/dropbox-api-v2-explorer/#sharing_list_folders), click **‘Get Token’**, then **‘Submit Call’**. Copy your folder’s `shared_folder_id`.",
        },
        {
          kind: "input",
          label: "Scopes",
          placeholder: "files.metadata.read files.content.read",
          required: true,
          target: { kind: "oauthConnection", field: "scopes" },
        },
        {
          kind: "input",
          label: "Root namespace ID (optional)",
          helperText:
            "Shared and team folders only; applies only to path-based operations.",
          target: { kind: "configuration", field: "namespaceId" },
        },
      ],
    },
    {
      title: "Authorize your Dropbox account",
      items: [
        {
          kind: "markdown",
          content: "Click below to authorize your Dropbox account.",
        },
        {
          kind: "oauth",
          dependsOn: [
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: () => "https://www.dropbox.com/oauth2/authorize",
    tokenEndpoint: () => "https://api.dropboxapi.com/oauth2/token",
    tokenEndpointAuthMethod: () => "client_secret_post",
    clientCredentialsFrom: "db",
    scopes: () => [],
    authorizationParameters: () => ({
      token_access_type: "offline",
    }),
  },
  connectionTest: {
    protocol: "http",
    request: () => ({
      url: "https://api.dropboxapi.com/2/check/user",
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
      expectedStatus: [200, 401, 403],
      expectedBodyContains: {
        401: '"missing_scope"',
        403: '"error_summary"',
      },
    }),
  },
});
